import { useState, useRef, useCallback } from 'react';
import {
  useGetTransportDrivers,
  useCreateTransportDriver,
  useUpdateTransportDriver,
  type TransportDriver,
  type TransportDriverPayload,
} from 'api/transportDriver/transportDriver';
import { isSuccessful } from 'api/utils';
import { appToast } from 'utils/toast.utils';
import { DIALOGS, DRIVER_FIELDS, ERRORS } from '../TransportCloudTable.consts';
import type { GroupedItems } from '../TransportCloudTable.types';
import { findRowAndOldValue } from './cellEditing/cellEditing.utils';

type DriverField = typeof DRIVER_FIELDS[number];

export type DriverValues = Record<DriverField, string>;

export interface DriverDbConfirmation {
  mode: 'add' | 'update';
  values: DriverValues;
  existingValues?: DriverValues;
  existingId?: number | null;
  itemId: string;
  columnKey: string;
}

const DRIVER_TO_FIELD_VALUE: Record<
  DriverField,
  (driver: TransportDriver) => string
> = {
  driverName: (driver) => driver.fullName,
  driverDocumentNumber: (driver) => driver.documentNumber,
  driverPhone: (driver) => driver.phone,
};

const toPayload = (values: DriverValues): TransportDriverPayload => ({
  fullName: values.driverName,
  documentNumber: values.driverDocumentNumber,
  phone: values.driverPhone,
});

export const useDriverDatabaseDialog = (
  groupedRowsRef: React.MutableRefObject<GroupedItems[]>,
  pendingSentValuesRef: React.MutableRefObject<Map<string, any>>,
) => {
  const [confirmation, setConfirmation] = useState<DriverDbConfirmation | null>(
    null,
  );

  const { getTransportDrivers: checkDriversReq } = useGetTransportDrivers();
  const { createTransportDriver } = useCreateTransportDriver();
  const { updateTransportDriver } = useUpdateTransportDriver();

  const checkRef = useRef<(itemId: string, columnKey: string) => void>(
    () => {},
  );

  checkRef.current = (itemId: string, columnKey: string) => {
    if (!(DRIVER_FIELDS as readonly string[]).includes(columnKey)) return;

    const { rowData } = findRowAndOldValue(
      groupedRowsRef.current,
      itemId,
      columnKey,
    );
    if (!rowData) return;

    const getEffective = (field: DriverField): string => {
      const pending = pendingSentValuesRef.current.get(`${itemId}:${field}`);
      return pending !== undefined
        ? String(pending)
        : (rowData as any)[field] ?? '';
    };

    const values: DriverValues = {
      driverName: getEffective('driverName'),
      driverDocumentNumber: getEffective('driverDocumentNumber'),
      driverPhone: getEffective('driverPhone'),
    };

    const allNonEmpty = Object.values(values).every((val) => !!val?.trim());
    const oldValue = String((rowData as any)[columnKey] ?? '');
    const newValue = values[columnKey as DriverField];
    const valueChanged = oldValue !== newValue;

    if (!allNonEmpty || !valueChanged) return;

    const editedField = columnKey as DriverField;
    const secondaryField: DriverField =
      editedField === 'driverPhone' ? 'driverDocumentNumber' : 'driverPhone';

    const findBestMatch = (drivers: TransportDriver[]) => {
      const matchCount = (driver: TransportDriver) =>
        DRIVER_FIELDS.filter(
          (field) => DRIVER_TO_FIELD_VALUE[field](driver) === values[field],
        ).length;
      return drivers
        .map((driver) => ({ driver: driver, count: matchCount(driver) }))
        .filter(({ count }) => count >= 2)
        .sort((a, b) => b.count - a.count)[0];
    };

    const applyMatch = (
      bestMatch: { driver: TransportDriver; count: number } | undefined,
    ) => {
      if (!bestMatch) {
        setConfirmation({ mode: 'add', values, itemId, columnKey });
        return;
      }
      if (bestMatch.count === 3) return;

      const { driver: existingDriver } = bestMatch;
      const existingValues: DriverValues = {
        driverName: DRIVER_TO_FIELD_VALUE.driverName(existingDriver),
        driverDocumentNumber:
          DRIVER_TO_FIELD_VALUE.driverDocumentNumber(existingDriver),
        driverPhone: DRIVER_TO_FIELD_VALUE.driverPhone(existingDriver),
      };
      setConfirmation({
        mode: 'update',
        values,
        existingValues,
        existingId: existingDriver.id,
        itemId,
        columnKey,
      });
    };

    checkDriversReq({
      filterText: values[editedField],
      resultSize: 15,
      pageNumber: 1,
    })
      .then((res: any) => {
        const firstMatch = findBestMatch(res?.data?.result?.data ?? []);
        if (firstMatch) {
          applyMatch(firstMatch);
          return;
        }
        return checkDriversReq({
          filterText: values[secondaryField],
          resultSize: 15,
          pageNumber: 1,
        }).then((res2: any) => {
          applyMatch(findBestMatch(res2?.data?.result?.data ?? []));
        });
      })
      .catch(() => {});
  };

  const handleConfirm = useCallback(async () => {
    if (!confirmation) return;

    const payload = toPayload(confirmation.values);
    const errorMessage =
      confirmation.mode === 'add'
        ? ERRORS.driverAddError
        : ERRORS.driverUpdateError;

    try {
      const res =
        confirmation.mode === 'add'
          ? await createTransportDriver(payload)
          : confirmation.existingId !== undefined &&
            confirmation.existingId !== null
          ? await updateTransportDriver(confirmation.existingId, payload)
          : undefined;

      if (res?.status && isSuccessful(res)) {
        appToast.success(
          confirmation.mode === 'add'
            ? DIALOGS.driverAddSuccessMessage
            : DIALOGS.driverUpdateSuccessMessage,
        );
      } else {
        appToast.error(errorMessage);
      }
    } catch {
      appToast.error(errorMessage);
    }

    setConfirmation(null);
  }, [confirmation, createTransportDriver, updateTransportDriver]);

  const handleCancel = useCallback(() => {
    setConfirmation(null);
  }, []);

  return { checkRef, confirmation, handleConfirm, handleCancel };
};
